import React, { useState, useMemo, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import {
  NAIS_CATALOG,
  NAIS_THEMES,
  NAIS_RESPONDENT_TYPES,
  NAIS_YEARS,
} from "./naisCatalog";
import {
  saveQuery,
  listMySavedQueries,
  listSharedQueries,
  updateSavedQuery,
  deleteSavedQuery,
  formatSavedAt,
} from "./naisSavedQueries";

/**
 * NAIS Explorer
 *
 * Five modes:
 *   💡 Insights — LLM-synthesized answers with citations (default)
 *   🔍 Data Finder — raw Pinecone chunks with scores
 *   📚 NAIS Reports — browseable catalog with PDF downloads
 *   ⭐ My Saves — this user's saved queries (private + their own shared)
 *   🌐 Shared — saves shared by other KA admins
 *
 * Super-Admin-only (enforced by SuperAdminRoute wrapper).
 */

const FN_BASE = "https://us-central1-kings-app-74847.cloudfunctions.net";
const INSIGHTS_URL = `${FN_BASE}/naisSynthesize`;
const DATA_FINDER_URL = `${FN_BASE}/queryNAISBenchmarks`;

const MODES = {
  INSIGHTS: "insights",
  DATA_FINDER: "data_finder",
  REPORTS: "reports",
  MY_SAVES: "my_saves",
  SHARED: "shared",
};

// ─── Download helper ─────────────────────────────────────────────────────────
async function downloadPDF(filename) {
  const storage = getStorage();
  const fileRef = ref(storage, `nais-reports/${filename}`);
  const url = await getDownloadURL(fileRef);
  window.open(url, "_blank", "noopener,noreferrer");
}

function DownloadButton({ filename, label = "View PDF", compact = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const handleClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    setError(null);
    try {
      await downloadPDF(filename);
    } catch (err) {
      console.error("PDF open failed:", err);
      setError("Failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`${compact ? "text-xs px-2 py-1" : "text-sm px-3 py-1.5"} rounded font-semibold disabled:opacity-50 transition-colors hover:opacity-90`}
      style={{ backgroundColor: "var(--ka-gold)", color: "var(--ka-navy)" }}
      title={`View ${filename}`}
    >
      {loading ? "..." : error ? `⚠ ${error}` : `📄 ${label}`}
    </button>
  );
}

// ─── Save form (appears inline after save button click) ─────────────────────
function SaveForm({ defaultTitle, onSave, onCancel, isSaving }) {
  const [title, setTitle] = useState(defaultTitle.slice(0, 80));
  const [note, setNote] = useState("");
  const [shared, setShared] = useState(false);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-3">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold mb-1 text-gray-700">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            disabled={isSaving}
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-gray-700">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="Why is this worth saving?"
            disabled={isSaving}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={shared}
            onChange={(e) => setShared(e.target.checked)}
            disabled={isSaving}
          />
          <span>Share with other KA admins</span>
        </label>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ title: title.trim() || defaultTitle.slice(0, 80), note: note.trim(), shared })}
            disabled={isSaving}
            className="px-4 py-1.5 text-sm rounded-md text-white font-semibold disabled:opacity-50"
            style={{ backgroundColor: "var(--ka-navy)" }}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Saved query card (rendered in My Saves + Shared tabs) ──────────────────
function SavedQueryCard({ item, isOwner, onLoad, onToggleShare, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editNote, setEditNote] = useState(item.note || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const commitEdit = async () => {
    setSavingEdit(true);
    try {
      await updateSavedQuery(item.id, { title: editTitle.trim(), note: editNote.trim() });
      item.title = editTitle.trim();
      item.note = editNote.trim();
      setEditing(false);
    } catch (err) {
      console.error(err);
      alert("Failed to save edits.");
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!window.confirm("Delete this saved query?")) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
    } catch (err) {
      console.error(err);
      alert("Failed to delete.");
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4">
        {editing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-semibold"
              disabled={savingEdit}
            />
            <textarea
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              rows={2}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              placeholder="Note..."
              disabled={savingEdit}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setEditTitle(item.title); setEditNote(item.note || ""); setEditing(false); }}
                className="px-3 py-1 text-xs rounded-md border border-gray-300"
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                onClick={commitEdit}
                className="px-3 py-1 text-xs rounded-md text-white"
                style={{ backgroundColor: "var(--ka-navy)" }}
                disabled={savingEdit}
              >
                {savingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => onLoad(item)}
                  className="text-left font-semibold text-lg hover:underline"
                  style={{ color: "var(--ka-navy)" }}
                >
                  {item.title}
                </button>
                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                  <span>{formatSavedAt(item.saved_at)}</span>
                  <span>•</span>
                  <span>{item.mode === "insights" ? "💡 Insights" : "🔍 Data Finder"}</span>
                  {!isOwner && item.userDisplayName && (
                    <>
                      <span>•</span>
                      <span>by {item.userDisplayName}</span>
                    </>
                  )}
                  {item.shared && (
                    <>
                      <span>•</span>
                      <span style={{ color: "var(--ka-navy)" }}>🌐 Shared</span>
                    </>
                  )}
                </div>
              </div>
              {isOwner && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setEditing(true)}
                    className="text-xs px-2 py-1 rounded hover:bg-gray-100"
                    title="Edit title/note"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => onToggleShare(item)}
                    className="text-xs px-2 py-1 rounded hover:bg-gray-100"
                    title={item.shared ? "Unshare" : "Share with admins"}
                  >
                    {item.shared ? "🔒" : "🌐"}
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deleting}
                    className="text-xs px-2 py-1 rounded hover:bg-red-50 text-red-600"
                    title="Delete"
                  >
                    {deleting ? "..." : "🗑"}
                  </button>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-700 italic mb-1">"{item.query}"</div>
            {item.note && (
              <div className="text-sm text-gray-600 mt-2 pl-3 border-l-2" style={{ borderColor: "var(--ka-gold)" }}>
                {item.note}
              </div>
            )}
            <button
              onClick={() => onLoad(item)}
              className="mt-3 text-xs underline"
              style={{ color: "var(--ka-navy)" }}
            >
              Open →
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function NAISExplorer() {
  const [mode, setMode] = useState(MODES.INSIGHTS);

  // Search state
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState("");
  const [respondentType, setRespondentType] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [topK, setTopK] = useState(10);

  // Results state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [insights, setInsights] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showSources, setShowSources] = useState(false);
  const [lastQuery, setLastQuery] = useState(null);

  // Saved view flags
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [savingNow, setSavingNow] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null); // docId of the just-saved item
  const [loadedFromSave, setLoadedFromSave] = useState(null); // the saved item currently displayed

  // Reports tab state
  const [expandedReports, setExpandedReports] = useState(new Set());
  const [reportsThemeFilter, setReportsThemeFilter] = useState("");
  const [reportsYearFilter, setReportsYearFilter] = useState("");

  // Saved queries state
  const [mySaves, setMySaves] = useState(null);
  const [sharedSaves, setSharedSaves] = useState(null);
  const [savesLoading, setSavesLoading] = useState(false);
  const [savesError, setSavesError] = useState(null);
  const [savesSearch, setSavesSearch] = useState("");

  const currentUid = getAuth().currentUser?.uid;

  const filtersActive = useMemo(() => {
    return Boolean(theme || respondentType || yearFrom || yearTo);
  }, [theme, respondentType, yearFrom, yearTo]);

  const buildFilters = () => {
    const filters = {};
    if (theme) filters.theme = theme;
    if (respondentType) filters.respondent_type = respondentType;
    if (yearFrom || yearTo) {
      filters.report_year = {};
      if (yearFrom) filters.report_year.$gte = parseInt(yearFrom, 10);
      if (yearTo) filters.report_year.$lte = parseInt(yearTo, 10);
    }
    return Object.keys(filters).length ? filters : undefined;
  };

  // TEMPORARY DIAGNOSTIC — remove after debugging
useEffect(() => {
  if (mode !== MODES.SHARED) return;
  (async () => {
    console.log('=== NAIS Saves Diagnostic ===');
    const { getFirestore, collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
    const { getAuth } = await import('firebase/auth');
    const user = getAuth().currentUser;
    console.log('Your email:', user?.email);
    console.log('Your uid:', user?.uid);
    console.log('Email matches KA regex:', /.*@kingsacademy[.]edu[.]jo/.test(user?.email || ''));

    const db = getFirestore();
    try {
      const q = query(collection(db, 'savedNAISQueries'), where('shared', '==', true), orderBy('saved_at', 'desc'));
      const snap = await getDocs(q);
      console.log('✓ Shared query OK:', snap.size, 'docs');
      snap.forEach(d => console.log('  -', d.id, 'userId:', d.data().userId, 'shared:', d.data().shared, 'title:', d.data().title));
    } catch (e) {
      console.error('✗ Shared query failed:');
      console.error('  code:', e.code);
      console.error('  message:', e.message);
      console.error('  full:', e);
    }
  })();
}, [mode]);

  const handleSearch = async () => {
    if (!query.trim() || query.trim().length < 3) {
      setError("Please enter a search query (3+ characters).");
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    setInsights(null);
    setExpandedIds(new Set());
    setShowSources(false);
    setShowSaveForm(false);
    setSaveSuccess(null);
    setLoadedFromSave(null);

    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in.");
      const idToken = await user.getIdToken();
      const filters = buildFilters();
      const isInsights = mode === MODES.INSIGHTS;
      const body = {
        query: query.trim(),
        topK: isInsights ? 8 : parseInt(topK, 10) || 10,
      };
      if (filters) body.filters = filters;
      const url = isInsights ? INSIGHTS_URL : DATA_FINDER_URL;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      if (isInsights) setInsights(data);
      else setResults(data);
      setLastQuery({ query: query.trim(), filters, mode });
    } catch (err) {
      console.error("NAIS query error:", err);
      setError(err.message || "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleReportExpand = (filename) => {
    setExpandedReports((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const clearFilters = () => {
    setTheme(""); setRespondentType(""); setYearFrom(""); setYearTo("");
  };

  const switchMode = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setError(null);
    setShowSaveForm(false);
    // Fetch saved data when switching to saves tabs
    if (newMode === MODES.MY_SAVES || newMode === MODES.SHARED) {
      fetchSaves(newMode);
    }
  };

  // ─── Saving ───────────────────────────────────────────────────────────────
  const handleSaveClick = () => {
    setShowSaveForm(true);
    setSaveSuccess(null);
  };

  const handleSaveConfirm = async ({ title, note, shared }) => {
    setSavingNow(true);
    try {
      const payload = {
        title,
        note,
        shared,
        query: lastQuery.query,
        mode: lastQuery.mode,
        filters: lastQuery.filters || null,
      };
      if (lastQuery.mode === MODES.INSIGHTS && insights) {
        payload.answer = insights.answer;
        payload.sources = insights.sources;
        payload.model = insights.model;
      } else if (lastQuery.mode === MODES.DATA_FINDER && results) {
        payload.hits = results.hits;
      }
      const docId = await saveQuery(payload);
      setSaveSuccess(docId);
      setShowSaveForm(false);
      // Invalidate saves cache so next visit refetches
      setMySaves(null);
    } catch (err) {
      console.error("Save failed:", err);
      alert(`Save failed: ${err.message}`);
    } finally {
      setSavingNow(false);
    }
  };

  // ─── Loading saved queries list ──────────────────────────────────────────
  const fetchSaves = useCallback(async (whichMode) => {
    setSavesLoading(true);
    setSavesError(null);
    try {
      if (whichMode === MODES.MY_SAVES) {
        const data = await listMySavedQueries();
        setMySaves(data);
      } else if (whichMode === MODES.SHARED) {
        const data = await listSharedQueries();
        setSharedSaves(data);
      }
    } catch (err) {
      console.error("Fetch saves failed:", err);
      setSavesError(err.message);
    } finally {
      setSavesLoading(false);
    }
  }, []);

  // Re-fetch when entering a saves tab if data is stale/null
  useEffect(() => {
    if (mode === MODES.MY_SAVES && mySaves === null) fetchSaves(MODES.MY_SAVES);
    if (mode === MODES.SHARED && sharedSaves === null) fetchSaves(MODES.SHARED);
  }, [mode, mySaves, sharedSaves, fetchSaves]);

  // ─── Loading a saved item into the main view ────────────────────────────
  const loadSavedItem = (item) => {
    setLoadedFromSave(item);
    setQuery(item.query);
    setTheme(item.filters?.theme || "");
    setRespondentType(item.filters?.respondent_type || "");
    setYearFrom(item.filters?.report_year?.$gte?.toString() || "");
    setYearTo(item.filters?.report_year?.$lte?.toString() || "");
    setLastQuery({ query: item.query, filters: item.filters, mode: item.mode });
    setExpandedIds(new Set());
    setShowSources(false);
    setError(null);

    if (item.mode === MODES.INSIGHTS) {
      setInsights({
        answer: item.answer,
        sources: item.sources,
        model: item.model,
        filters_applied: item.filters,
        query: item.query,
      });
      setResults(null);
      setMode(MODES.INSIGHTS);
    } else {
      setResults({
        hits: item.hits || [],
        query: item.query,
        filters_applied: item.filters,
      });
      setInsights(null);
      setMode(MODES.DATA_FINDER);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggleShare = async (item) => {
    try {
      await updateSavedQuery(item.id, { shared: !item.shared });
      item.shared = !item.shared;
      // Force re-render by creating a new array reference
      setMySaves((prev) => prev ? [...prev] : prev);
      setSharedSaves(null); // invalidate shared cache
    } catch (err) {
      console.error("Toggle share failed:", err);
      alert("Failed to change sharing status.");
    }
  };

  const handleDelete = async (docId) => {
    await deleteSavedQuery(docId);
    setMySaves((prev) => prev ? prev.filter((s) => s.id !== docId) : prev);
    setSharedSaves(null);
  };

  // ─── Filtered catalog for Reports tab ───────────────────────────────────
  const filteredCatalog = useMemo(() => {
    return NAIS_CATALOG.filter((r) => {
      if (reportsThemeFilter && r.theme !== reportsThemeFilter) return false;
      if (reportsYearFilter && r.year !== parseInt(reportsYearFilter, 10)) return false;
      return true;
    });
  }, [reportsThemeFilter, reportsYearFilter]);

  const reportsByTheme = useMemo(() => {
    const grouped = {};
    for (const report of filteredCatalog) {
      if (!grouped[report.theme]) grouped[report.theme] = [];
      grouped[report.theme].push(report);
    }
    return Object.entries(grouped).sort((a, b) => {
      if (b[1].length !== a[1].length) return b[1].length - a[1].length;
      return a[0].localeCompare(b[0]);
    });
  }, [filteredCatalog]);

  // ─── Filtered saves list (text search) ──────────────────────────────────
  const filteredSaves = useMemo(() => {
    const source = mode === MODES.MY_SAVES ? mySaves : sharedSaves;
    if (!source) return null;
    if (!savesSearch.trim()) return source;
    const q = savesSearch.toLowerCase();
    return source.filter((item) =>
      (item.title || "").toLowerCase().includes(q)
      || (item.query || "").toLowerCase().includes(q)
      || (item.note || "").toLowerCase().includes(q)
    );
  }, [mode, mySaves, sharedSaves, savesSearch]);

  const isSearchMode = mode === MODES.INSIGHTS || mode === MODES.DATA_FINDER;
  const isSavesMode = mode === MODES.MY_SAVES || mode === MODES.SHARED;
  const hasResults = Boolean(insights || (results && results.hits));
  const canSave = hasResults && lastQuery && !loadedFromSave;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ka-cream)" }}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-1" style={{ color: "var(--ka-navy)" }}>
            NAIS Explorer
          </h1>
          <p className="text-gray-600 text-sm">
            {mode === MODES.INSIGHTS && "AI-synthesized answers with citations from 52 NAIS Snapshot Surveys (2021–2026)."}
            {mode === MODES.DATA_FINDER && "Raw chunk retrieval from 707 indexed page-excerpts."}
            {mode === MODES.REPORTS && "Browse the full library of 52 NAIS Snapshot Surveys."}
            {mode === MODES.MY_SAVES && "Your saved queries and answers."}
            {mode === MODES.SHARED && "Saved queries shared by other KA admins."}
          </p>
        </div>

        {/* Mode toggle */}
        <div className="mb-6 flex flex-wrap gap-1 rounded-md overflow-hidden border" style={{ borderColor: "var(--ka-navy)", display: "inline-flex" }}>
          {[
            { id: MODES.INSIGHTS, label: "💡 Insights" },
            { id: MODES.DATA_FINDER, label: "🔍 Data Finder" },
            { id: MODES.REPORTS, label: "📚 NAIS Reports" },
            { id: MODES.MY_SAVES, label: "⭐ My Saves" },
            { id: MODES.SHARED, label: "🌐 Shared" },
          ].map((m, i, arr) => (
            <button
              key={m.id}
              onClick={() => switchMode(m.id)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${i < arr.length - 1 ? "border-r" : ""}`}
              style={{
                backgroundColor: mode === m.id ? "var(--ka-navy)" : "transparent",
                color: mode === m.id ? "white" : "var(--ka-navy)",
                borderRightColor: "var(--ka-navy)",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Loaded-from-save banner */}
        {loadedFromSave && isSearchMode && (
          <div className="bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded-md mb-4 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <strong>Loaded saved query</strong> from {formatSavedAt(loadedFromSave.saved_at)}
              {loadedFromSave.userId !== currentUid && loadedFromSave.userDisplayName && <> by {loadedFromSave.userDisplayName}</>}.
              This answer reflects the NAIS Surveys at that time.
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="text-xs px-3 py-1 rounded bg-white border border-blue-300 hover:bg-blue-100"
              >
                Re-run this query
              </button>
              <button
                onClick={() => setLoadedFromSave(null)}
                className="text-xs px-3 py-1 rounded bg-white border border-blue-300 hover:bg-blue-100"
              >
                Dismiss banner
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* SEARCH MODES (Insights + Data Finder)                                */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {isSearchMode && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <label className="block text-sm font-semibold mb-2" style={{ color: "var(--ka-navy)" }}>
                Your question
              </label>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !loading) handleSearch(); }}
                  placeholder={mode === MODES.INSIGHTS
                    ? 'e.g. "How has teacher retention changed over the past few years?"'
                    : 'e.g. "teacher retention contract renewal rates"'}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ "--tw-ring-color": "var(--ka-gold)" }}
                  disabled={loading}
                />
                <button
                  onClick={handleSearch}
                  disabled={loading || !query.trim()}
                  className="px-6 py-2 rounded-md font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "var(--ka-navy)" }}
                >
                  {loading ? (mode === MODES.INSIGHTS ? "Thinking..." : "Searching...") : (mode === MODES.INSIGHTS ? "Ask" : "Search")}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">Theme</label>
                  <select value={theme} onChange={(e) => setTheme(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" disabled={loading}>
                    <option value="">Any theme</option>
                    {NAIS_THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">Respondent type</label>
                  <select value={respondentType} onChange={(e) => setRespondentType(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" disabled={loading}>
                    <option value="">Any respondent</option>
                    {NAIS_RESPONDENT_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">Year from</label>
                  <select value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" disabled={loading}>
                    <option value="">No minimum</option>
                    {NAIS_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">Year to</label>
                  <select value={yearTo} onChange={(e) => setYearTo(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" disabled={loading}>
                    <option value="">No maximum</option>
                    {NAIS_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center gap-3">
                  {mode === MODES.DATA_FINDER && (
                    <label>
                      Top results:
                      <select value={topK} onChange={(e) => setTopK(e.target.value)} className="ml-1 border border-gray-300 rounded px-1 py-0.5" disabled={loading}>
                        {[5, 10, 15, 20, 25].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                  )}
                  {filtersActive && (
                    <button onClick={clearFilters} className="text-xs underline" style={{ color: "var(--ka-navy)" }}>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">{error}</div>
            )}

            {loading && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                {mode === MODES.INSIGHTS ? "Searching the NAIS Surveys and synthesizing an answer..." : "Searching..."}
              </div>
            )}

            {/* Save form / success banner — shown above results */}
            {!loading && canSave && showSaveForm && (
              <SaveForm
                defaultTitle={lastQuery.query}
                onSave={handleSaveConfirm}
                onCancel={() => setShowSaveForm(false)}
                isSaving={savingNow}
              />
            )}
            {!loading && hasResults && saveSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded-md mb-4 text-sm flex items-center justify-between">
                <span>✓ Saved. You can find it in ⭐ My Saves.</span>
                <button onClick={() => setSaveSuccess(null)} className="text-xs underline">Dismiss</button>
              </div>
            )}

            {/* Insights results */}
            {!loading && insights && (
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold" style={{ color: "var(--ka-navy)" }}>Answer</h2>
                      {lastQuery?.filters && (
                        <span className="text-xs text-gray-500">
                          filtered: <code className="bg-gray-100 px-1 rounded">{JSON.stringify(lastQuery.filters)}</code>
                        </span>
                      )}
                    </div>
                    {canSave && !showSaveForm && !saveSuccess && (
                      <button
                        onClick={handleSaveClick}
                        className="text-xs px-3 py-1 rounded font-semibold hover:opacity-90"
                        style={{ backgroundColor: "var(--ka-gold)", color: "var(--ka-navy)" }}
                      >
                        ⭐ Save this
                      </button>
                    )}
                  </div>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800 leading-relaxed">
                    {insights.answer}
                  </div>
                </div>

                {insights.sources && insights.sources.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm">
                    <button
                      onClick={() => setShowSources(!showSources)}
                      className="w-full text-left px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                      style={{ color: "var(--ka-navy)" }}
                    >
                      <span className="font-semibold text-sm">📚 Sources used ({insights.sources.length})</span>
                      <span className="text-xs">{showSources ? "▲ Hide" : "▼ Show"}</span>
                    </button>
                    {showSources && (
                      <div className="px-6 pb-4 space-y-3 border-t border-gray-100">
                        {insights.sources.map((hit, i) => {
                          const expanded = expandedIds.has(hit.id);
                          return (
                            <div key={hit.id} className="pt-3 border-b border-gray-100 pb-3 last:border-b-0">
                              <div className="flex items-start justify-between gap-3 mb-1">
                                <div className="flex-1">
                                  <div className="text-sm font-semibold" style={{ color: "var(--ka-navy)" }}>
                                    [{i + 1}] {hit.report_title} ({hit.report_year})
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {hit.report_date} • p.{hit.page_number}
                                    {hit.respondent_type && ` • ${hit.respondent_count ? `n=${hit.respondent_count} ` : ""}${hit.respondent_type}`}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: "var(--ka-gold)", color: "var(--ka-navy)" }}>
                                    {hit.score?.toFixed(3)}
                                  </div>
                                  {hit.source_filename && (
                                    <DownloadButton filename={hit.source_filename} label="PDF" compact />
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-gray-700 whitespace-pre-wrap mt-1">
                                {expanded ? hit.text : hit.text.length > 200 ? hit.text.slice(0, 200) + "…" : hit.text}
                              </p>
                              {hit.text.length > 200 && (
                                <button onClick={() => toggleExpand(hit.id)} className="mt-1 text-xs underline" style={{ color: "var(--ka-navy)" }}>
                                  {expanded ? "Show less" : "Show full text"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Data Finder results */}
            {!loading && results && (
              <div>
                <div className="mb-4 text-sm text-gray-600 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    {results.hits.length > 0 ? (
                      <>
                        Found <strong>{results.hits.length}</strong> result{results.hits.length !== 1 ? "s" : ""} for <em>"{lastQuery?.query}"</em>
                        {lastQuery?.filters && <> with filters: <code className="bg-gray-100 px-1 py-0.5 rounded">{JSON.stringify(lastQuery.filters)}</code></>}
                      </>
                    ) : (<>No results found. Try broadening your query or removing filters.</>)}
                  </div>
                  {canSave && results.hits.length > 0 && !showSaveForm && !saveSuccess && (
                    <button
                      onClick={handleSaveClick}
                      className="text-xs px-3 py-1 rounded font-semibold hover:opacity-90"
                      style={{ backgroundColor: "var(--ka-gold)", color: "var(--ka-navy)" }}
                    >
                      ⭐ Save this
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {results.hits.map((hit) => {
                    const expanded = expandedIds.has(hit.id);
                    return (
                      <div key={hit.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg" style={{ color: "var(--ka-navy)" }}>
                                {hit.report_title}
                              </h3>
                              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                <span>{hit.report_date}</span>
                                <span>•</span>
                                <span>Page {hit.page_number}</span>
                                {hit.theme && (
                                  <>
                                    <span>•</span>
                                    <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: "var(--ka-cream)", color: "var(--ka-navy)" }}>
                                      {hit.theme}
                                    </span>
                                  </>
                                )}
                                {hit.respondent_type && (
                                  <>
                                    <span>•</span>
                                    <span>{hit.respondent_count ? `n=${hit.respondent_count} ` : ""}{hit.respondent_type}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div
                                className="text-xs font-mono px-2 py-1 rounded"
                                style={{ backgroundColor: "var(--ka-gold)", color: "var(--ka-navy)" }}
                                title="Relevance score"
                              >
                                {hit.score?.toFixed(3)}
                              </div>
                              {hit.source_filename && (
                                <DownloadButton filename={hit.source_filename} compact />
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {expanded ? hit.text : hit.text.length > 300 ? hit.text.slice(0, 300) + "…" : hit.text}
                          </p>
                          {hit.text.length > 300 && (
                            <button onClick={() => toggleExpand(hit.id)} className="mt-2 text-xs underline" style={{ color: "var(--ka-navy)" }}>
                              {expanded ? "Show less" : "Show full text"}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && !results && !insights && !error && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                <p className="mb-2">
                  {mode === MODES.INSIGHTS
                    ? "Ask a question about NAIS benchmark data. You'll get a synthesized answer with citations."
                    : "Search NAIS data. You'll get the raw matching page-excerpts ranked by relevance."}
                </p>
                <p className="text-xs">
                  {mode === MODES.INSIGHTS
                    ? 'Examples: "How do teacher retention rates compare across 2022 to 2026?" or "What AI policies do NAIS schools have?"'
                    : 'Examples: "teacher retention trends", "head of school burnout", "phone policies in elementary schools"'}
                </p>
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* NAIS REPORTS MODE                                                    */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {mode === MODES.REPORTS && (
          <>
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap items-center gap-3">
              <div className="text-sm text-gray-600 mr-2">Showing <strong>{filteredCatalog.length}</strong> of {NAIS_CATALOG.length} reports</div>
              <select value={reportsThemeFilter} onChange={(e) => setReportsThemeFilter(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                <option value="">All themes</option>
                {NAIS_THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={reportsYearFilter} onChange={(e) => setReportsYearFilter(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                <option value="">All years</option>
                {NAIS_YEARS.slice().reverse().map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              {(reportsThemeFilter || reportsYearFilter) && (
                <button onClick={() => { setReportsThemeFilter(""); setReportsYearFilter(""); }} className="text-xs underline" style={{ color: "var(--ka-navy)" }}>
                  Clear filters
                </button>
              )}
            </div>

            {reportsByTheme.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">No reports match the current filters.</div>
            ) : (
              <div className="space-y-6">
                {reportsByTheme.map(([themeName, reports]) => (
                  <div key={themeName} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-5 py-3 font-semibold flex items-center justify-between" style={{ backgroundColor: "var(--ka-navy)", color: "white" }}>
                      <span>{themeName}</span>
                      <span className="text-xs opacity-80">{reports.length} report{reports.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {reports.map((report) => {
                        const isExpanded = expandedReports.has(report.filename);
                        return (
                          <div key={report.filename} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between gap-3">
                              <button onClick={() => toggleReportExpand(report.filename)} className="flex-1 text-left">
                                <div className="font-semibold" style={{ color: "var(--ka-navy)" }}>{report.title}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {report.date} • {report.pages} pages • {Math.round(report.size_kb)} KB
                                  <span className="ml-2 text-gray-400">{isExpanded ? "▲" : "▼"}</span>
                                </div>
                              </button>
                              <DownloadButton filename={report.filename} label="PDF" compact />
                            </div>
                            {isExpanded && report.blurb && (
                              <div className="mt-3 pl-3 border-l-2 text-sm text-gray-700 leading-relaxed" style={{ borderColor: "var(--ka-gold)" }}>
                                {report.blurb}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* SAVED QUERIES MODES (My Saves + Shared)                              */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {isSavesMode && (
          <>
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <input
                type="text"
                value={savesSearch}
                onChange={(e) => setSavesSearch(e.target.value)}
                placeholder="Search titles, queries, and notes..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": "var(--ka-gold)" }}
              />
            </div>

            {savesError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6 text-sm">
                {savesError}
                {savesError.includes("index") && (
                  <div className="mt-2 text-xs">
                    Firestore may need a composite index. Check the browser console for a creation link, or deploy <code>firestore.indexes.json</code>.
                  </div>
                )}
              </div>
            )}

            {savesLoading && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">Loading...</div>
            )}

            {!savesLoading && filteredSaves && filteredSaves.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                {savesSearch ? (
                  <>No saves match "{savesSearch}".</>
                ) : mode === MODES.MY_SAVES ? (
                  <>
                    <p className="mb-2">You haven't saved any queries yet.</p>
                    <p className="text-xs">When you run a query in Insights or Data Finder, click ⭐ Save this to bookmark it.</p>
                  </>
                ) : (
                  <>
                    <p className="mb-2">No shared saves from other admins yet.</p>
                    <p className="text-xs">When admins share saves from their own collection, they'll appear here.</p>
                  </>
                )}
              </div>
            )}

            {!savesLoading && filteredSaves && filteredSaves.length > 0 && (
              <div className="space-y-3">
                <div className="text-xs text-gray-500 mb-2">
                  {filteredSaves.length} {filteredSaves.length === 1 ? "save" : "saves"}
                  {savesSearch && ` matching "${savesSearch}"`}
                </div>
                {filteredSaves.map((item) => (
                  <SavedQueryCard
                    key={item.id}
                    item={item}
                    isOwner={item.userId === currentUid}
                    onLoad={loadSavedItem}
                    onToggleShare={handleToggleShare}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}